/**
 * The Sources tab: which connections feed this graph, and what each one reads.
 *
 * One row per source, and a source is one connection — however many tables,
 * folders or spaces it reads inside it. Those live on the row as a count and
 * are listed when you open it, because "the warehouse" is the thing a person
 * thinks about, and "the warehouse's four tables" is what they want when they
 * look closer.
 *
 * Opening a source shows exactly what was configured: where it comes from,
 * what it reads, and what each object becomes. None of *that* is editable on
 * the panel — **Edit** reopens the same four-step flow that created it, so the
 * form you change something in is the form you made it in.
 *
 * Auto-sync is the exception, and the line it draws is between configuration
 * and operation. What a source reads is a decision about the model, made once
 * and changed rarely; how often it is re-read is a dial you turn while watching
 * it, the same kind of thing as the Publish button that already lives on the
 * row. Sending someone back through four steps to change a number is how a
 * setting stops being used.
 */

import { useState } from 'react'
import { EmptyState, Logo, SkeletonRows, relativeTime, untilTime, useNow } from '@/shared/components'
import { useToast } from '@/shared/components/toast'
import { Chip, Drawer } from '../components'
import { IngestDialog } from './IngestDialog'
import { SourceWizard } from './SourceWizard'
import { useSourceMutations, useSources } from '../hooks'
import type { BrainSource, GraphModel, SourceSync, SyncMode } from '../types/brain.types'

/**
 * Intervals someone might actually want, in the words they would say.
 *
 * Not a free number field: every value below the floor is refused by the server
 * anyway, and a box that accepts 30 and silently stores 300 is worse than a
 * list that never offered 30.
 */
const INTERVALS = [
  { seconds: 60, label: 'every minute' },
  { seconds: 300, label: 'every 5 minutes' },
  { seconds: 900, label: 'every 15 minutes' },
  { seconds: 1800, label: 'every 30 minutes' },
  { seconds: 3600, label: 'every hour' },
  { seconds: 21600, label: 'every 6 hours' },
  { seconds: 86400, label: 'once a day' },
]

/** `900` as `15m`. */
function formatInterval(seconds: number | null): string {
  const value = seconds ?? 900
  if (value % 86400 === 0) return `${value / 86400}d`
  if (value % 3600 === 0) return `${value / 3600}h`
  return `${Math.round(value / 60)}m`
}

/** How a source's last run reads at a glance. */
function runTone(status: string): 'ok' | 'plain' | 'err' {
  if (status === 'ok') return 'ok'
  if (status === 'never') return 'plain'
  return 'err'
}

export function SourcesTab({ graphId, model }: { graphId: string; model: GraphModel }) {
  const sources = useSources(graphId)
  const { detach, publish, resetSync, update } = useSourceMutations(graphId)
  const toast = useToast()
  // Keeps "next check in 40s" and "up to 2m ago" moving between refetches.
  useNow(10_000)

  const [connecting, setConnecting] = useState(false)
  const [editing, setEditing] = useState<BrainSource | null>(null)
  const [ingesting, setIngesting] = useState<BrainSource | null>(null)
  const [opened, setOpened] = useState<string | null>(null)
  const [publishingId, setPublishingId] = useState<string | null>(null)

  const attached = sources.data?.sources ?? []
  const open = attached.find((s) => s.id === opened) ?? null

  /** Fetch everything this source reads, then run the rules over it. */
  async function publishOne(source: BrainSource) {
    if (source.objects.length === 0) {
      toast.error(
        new Error('This source has no object to read. Edit it and choose one.'),
        'Nothing to publish',
      )
      return
    }
    setPublishingId(source.id)
    try {
      const outcome = await publish.mutateAsync({ id: source.id })
      const buckets = (outcome.counts?.buckets ?? {}) as Record<string, number>
      if (outcome.failed.length) {
        toast.error(
          new Error(outcome.failed.map((f) => f.error ?? 'failed').join('; ')),
          `${outcome.published} of ${outcome.objects.length} objects fetched`,
        )
      } else {
        const forward = outcome.objects.reduce((n, o) => n + (o.sync?.forward_rows ?? 0), 0)
        const history = outcome.objects.reduce((n, o) => n + (o.sync?.backfill_rows ?? 0), 0)
        const more = outcome.objects.some((o) => o.sync?.more)
        toast.success(
          `Published ${source.display_name}`,
          `${outcome.staged} new of ${forward + history} read (${forward} changed, ${history} history)` +
            `${more ? ' · more history next pass' : ''}` +
            ` · ${buckets.rejected ?? 0} rejected · ${buckets.potential ?? 0} waiting for review`,
        )
      }
    } catch (error) {
      toast.error(error)
    } finally {
      setPublishingId(null)
    }
  }

  return (
    <div className="brain-panel">
      <div className="brain-colstack">
        <div className="cp-toolbar">
          <h1>Sources</h1>
          <Chip>
            {attached.length} feeding this graph
          </Chip>
          <span className="cp-spacer" />
          <button className="btn btn-primary" onClick={() => setConnecting(true)}>
            Connect a source
          </button>
          <div className="sub2">
            Four steps: pick the app, pick the connection, pick what to read inside it, and say
            which node type each object becomes. Saving writes the mapping and fetches nothing;
            publishing a source fetches everything it reads.
          </div>
        </div>

        {sources.isLoading && <SkeletonRows rows={4} columns={5} label="Loading sources…" />}

        {!sources.isLoading && attached.length === 0 && (
          <EmptyState
            title="No sources connected"
            action={
              <button className="btn btn-primary" onClick={() => setConnecting(true)}>
                Connect a source
              </button>
            }
          >
            Connect one to start bringing in records. You will pick the app, the connection to it,
            what to read inside it, and which node type each object becomes.
          </EmptyState>
        )}

        {attached.length > 0 && (
          <div className="cp-card cp-tablewrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>System</th>
                  <th>Reads</th>
                  <th>Staged</th>
                  <th>Sync</th>
                  <th>Last run</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {attached.map((source) => (
                  <tr key={source.id} data-clickable="true" onClick={() => setOpened(source.id)}>
                    <td>
                      <span className="cp-rowtitle">
                        <Logo name={source.display_name} id={source.connector_id} small />
                        <span className="truncate">{source.display_name}</span>
                      </span>
                      <div className="mono small faint">{source.source_id}</div>
                    </td>
                    <td className="muted small">{source.connector_id}</td>
                    <td>
                      {/* The objects it reads, named. A count alone would make
                          you open the panel to learn what it does. */}
                      {source.objects.length === 0 ? (
                        <Chip tone="warn">nothing chosen</Chip>
                      ) : (
                        <div className="row-tight" style={{ flexWrap: 'wrap', gap: 4 }}>
                          {source.objects.slice(0, 3).map((object) => (
                            <Chip key={object.action_id} tone="plain">
                              {object.label ?? object.action_id}
                              {object.node_type_key ? ` → ${object.node_type_key}` : ''}
                            </Chip>
                          ))}
                          {source.objects.length > 3 && (
                            <span className="faint small">
                              +{source.objects.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="num">{source.staged_count.toLocaleString()}</td>
                    <td>
                      {source.sync_mode === 'poll' ? (
                        <>
                          <Chip tone="ok">every {formatInterval(source.poll_interval_s)}</Chip>
                          <div className="faint small">
                            next check {source.next_poll_at ? untilTime(source.next_poll_at) : 'at the next minute'}
                          </div>
                        </>
                      ) : (
                        <Chip tone="plain">when published</Chip>
                      )}
                      <SyncProgress sync={source.sync ?? []} />
                    </td>
                    <td>
                      <Chip tone={runTone(source.last_run_status)}>
                        {source.last_run_status === 'never' ? 'never run' : source.last_run_status}
                      </Chip>
                      <div className="faint small">{relativeTime(source.last_run_at)}</div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {/* This source only. Publishing the graph — every source
                          at once — is the Publish button in the header above. */}
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => publishOne(source)}
                        disabled={publishingId === source.id}
                        title="Fetch everything this source reads and run it through the rules."
                      >
                        {publishingId === source.id ? 'Publishing…' : 'Publish'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={Boolean(open)} onClose={() => setOpened(null)}>
        {open && (
          <SourceSummary
            source={open}
            onClose={() => setOpened(null)}
            onSync={async (body) => {
              try {
                await update.mutateAsync({ id: open.id, body })
                toast.success(
                  body.sync_mode === 'poll' ? 'Auto-sync on' : 'Auto-sync off',
                  body.sync_mode === 'poll'
                    ? 'This source will be re-read on its own from now on.'
                    : 'This source is only read when you publish it.',
                )
              } catch (error) {
                toast.error(error)
              }
            }}
            onReset={async () => {
              if (
                !window.confirm(
                  `Start '${open.display_name}' over from the top? Rows already in the graph stay; the next pass re-reads from the beginning.`,
                )
              )
                return
              try {
                await resetSync.mutateAsync(open.id)
                toast.success('Sync reset', 'The next pass starts from the top.')
              } catch (error) {
                toast.error(error)
              }
            }}
            onEdit={() => {
              setEditing(open)
              setOpened(null)
            }}
            onIngest={() => {
              setIngesting(open)
              setOpened(null)
            }}
            onDetach={async () => {
              if (
                !window.confirm(
                  `Stop reading from '${open.display_name}'? Records already built stay in the graph.`,
                )
              )
                return
              try {
                await detach.mutateAsync(open.id)
                setOpened(null)
                toast.success('Source detached.')
              } catch (error) {
                toast.error(error)
              }
            }}
          />
        )}
      </Drawer>

      {connecting && (
        <SourceWizard graphId={graphId} model={model} onClose={() => setConnecting(false)} />
      )}

      {editing && (
        <SourceWizard
          graphId={graphId}
          model={model}
          editing={editing}
          onClose={() => setEditing(null)}
        />
      )}

      {ingesting && (
        <IngestDialog graphId={graphId} source={ingesting} onClose={() => setIngesting(null)} />
      )}
    </div>
  )
}

/**
 * One source, exactly as it was configured.
 *
 * Read-only on purpose. Everything here was decided in the four-step flow, and
 * a second place to change it is a second place for the two to disagree —
 * Edit reopens that flow with these answers already filled in.
 */
function SourceSummary({
  source,
  onClose,
  onEdit,
  onIngest,
  onDetach,
  onReset,
  onSync,
}: {
  source: BrainSource
  onClose: () => void
  onEdit: () => void
  onIngest: () => void
  onDetach: () => void
  onReset: () => void
  onSync: (body: { sync_mode: SyncMode; poll_interval_s?: number }) => void
}) {
  const polling = source.sync_mode === 'poll'
  const interval = source.poll_interval_s ?? 300
  const syncs = source.sync ?? []
  const started = syncs.some((s) => s.high_water)
  return (
    <>
      <div className="cp-drhead">
        <div className="cp-drtop">
          <Logo name={source.display_name} id={source.connector_id} small />
          <span className="cp-drname truncate">{source.display_name}</span>
          <Chip tone={runTone(source.last_run_status)}>
            {source.last_run_status === 'never' ? 'never run' : source.last_run_status}
          </Chip>
          <button className="cp-iconbtn" style={{ marginLeft: 'auto' }} onClick={onClose}>
            ✕
          </button>
        </div>
        <p className="cp-drmeta">
          {source.staged_count.toLocaleString()} staged · last run{' '}
          {relativeTime(source.last_run_at) || '—'}
        </p>
      </div>

      <div className="cp-drbody">
        <div className="stack">
          <div className="cp-sect">
            <h4>Where it comes from</h4>
            <div className="cp-erow">
              <span className="lab">connector</span>
              <span className="to mono">{source.connector_id}</span>
            </div>
            <div className="cp-erow">
              <span className="lab">source id</span>
              <span className="to mono small truncate">{source.source_id}</span>
            </div>
            <div className="cp-erow">
              <span className="lab">read as</span>
              <span className="to mono">
                {source.mode} · {source.kind}
              </span>
            </div>
          </div>

          <div className="cp-sect">
            <h4>Keeping up</h4>
            <p className="small faint" style={{ margin: '0 0 8px' }}>
              Every pass — a publish, or a tick of auto-sync — does two things: reads what
              changed since the last pass, and reads one more page budget of older history
              until the whole collection is in. Rows already seen are skipped, so a pass
              costs calls, never duplicates.
            </p>

            <label className="ag-graphrow" data-on={polling}>
              <input
                type="checkbox"
                checked={polling}
                onChange={() =>
                  onSync(
                    polling
                      ? { sync_mode: 'batch' }
                      : { sync_mode: 'poll', poll_interval_s: interval },
                  )
                }
              />
              <span className="ag-graphname">Keep this source in sync automatically</span>
            </label>

            {polling && (
              <>
                <div className="cp-erow" style={{ marginTop: 8 }}>
                  <span className="lab">how often</span>
                  <span className="to">
                    <select
                      className="select"
                      value={interval}
                      onChange={(e) =>
                        onSync({
                          sync_mode: 'poll',
                          poll_interval_s: Number(e.target.value),
                        })
                      }
                    >
                      {INTERVALS.map((option) => (
                        <option key={option.seconds} value={option.seconds}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </span>
                </div>
                <div className="cp-erow">
                  <span className="lab">next check</span>
                  <span className="to small">
                    {source.next_poll_at ? untilTime(source.next_poll_at) : 'at the next minute'}
                  </span>
                </div>
              </>
            )}

            {source.last_error && (
              <p className="small" style={{ margin: '8px 0 0', color: 'var(--danger)' }}>
                {source.last_error}
              </p>
            )}

            {syncs.length > 0 && (
              <div className="stack" style={{ gap: 6, marginTop: 10 }}>
                {syncs.map((s) => (
                  <SyncRow key={s.key} sync={s} />
                ))}
                {started && (
                  <button
                    className="btn btn-sm btn-ghost"
                    style={{ alignSelf: 'flex-start' }}
                    onClick={onReset}
                    title="Forget the marks; the next pass re-reads from the top. Rows already staged stay."
                  >
                    Start over from the top
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="cp-sect">
            <h4>
              What it reads
              {source.objects.length > 0 && (
                <span className="faint small"> · {source.objects.length}</span>
              )}
            </h4>

            {source.objects.length === 0 && (
              <p className="small faint" style={{ margin: 0 }}>
                Nothing chosen yet. Edit this source and pick what to read.
              </p>
            )}

            {source.objects.map((object) => {
              const mapped = Object.entries(object.mapping ?? {}).filter(([, to]) => to)
              return (
                <div className="wz-map" key={object.action_id} style={{ marginTop: 8 }}>
                  <div className="wz-maphead">
                    <strong>{object.label ?? object.action_id}</strong>
                    <span className="faint small">→</span>
                    {object.node_type_key ? (
                      <Chip tone="core">{object.node_type_key}</Chip>
                    ) : (
                      <Chip tone="warn">no node type</Chip>
                    )}
                  </div>

                  {Object.keys(object.input ?? {}).length > 0 && (
                    <div className="cp-erow">
                      <span className="lab">input</span>
                      <span className="to mono small">
                        {Object.entries(object.input)
                          .map(([k, v]) => `${k}: ${String(v)}`)
                          .join(' · ')}
                      </span>
                    </div>
                  )}

                  {(object.links ?? []).length > 0 && (
                    <div className="stack" style={{ gap: 3 }}>
                      <span className="small faint">Relationships</span>
                      {object.links.map((link) => (
                        <div className="cp-erow" key={link.property_key}>
                          <span className="lab mono">{link.property_key}</span>
                          <span className="to small">
                            {link.edge_type_key} → {link.to_node_type}.{link.to_property}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {mapped.length === 0 ? (
                    <p className="small faint" style={{ margin: 0 }}>
                      No mapping — columns arrive under their own names.
                    </p>
                  ) : (
                    <div className="table-wrap">
                      <table className="cp-table">
                        <thead>
                          <tr>
                            <th>Source column</th>
                            <th>Becomes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mapped.map(([from, to]) => (
                            <tr key={from}>
                              <td className="mono small">{from}</td>
                              <td className="mono small">{to}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          <div className="row-tight">
            <button className="btn btn-primary btn-sm" onClick={onEdit}>
              Edit
            </button>
            <button className="btn btn-sm" onClick={onIngest}>
              Paste rows
            </button>
            <span className="cp-spacer" />
            <button className="btn btn-ghost btn-sm" onClick={onDetach}>
              Stop using this source
            </button>
          </div>
        </div>
      </div>
    </>
  )
}


// --- sync progress ---------------------------------------------------------------------

/** One line under the schedule: how much of the collection is in, and how fresh. */
function SyncProgress({ sync }: { sync: SourceSync[] }) {
  if (!sync.length || !sync.some((s) => s.high_water)) return null
  const historyDone = sync.every((s) => s.backfill_done || !s.paginated)
  const behind = sync.some((s) => s.forward)
  const newest = sync
    .map((s) => s.high_water)
    .filter((v): v is string => Boolean(v))
    .sort()
    .pop()
  return (
    <div className="faint small" title={sync.map(describeSync).join('\n')}>
      {historyDone ? 'history complete' : 'history loading…'}
      {newest ? ` · up to ${relativeTime(newest)}` : ''}
      {behind ? ' · catching up' : ''}
    </div>
  )
}

function describeSync(s: SourceSync): string {
  const how = {
    filter: 'asks for rows changed since the mark',
    newest_first: 'reads newest-first pages until it reaches rows already seen',
    sweep: 're-walks the whole collection a page budget at a time',
    whole: 're-reads the whole result every pass',
  }[s.strategy]
  return `${s.label}: ${how}`
}

/** Per object: the mark, the history, and how it keeps up. */
function SyncRow({ sync: s }: { sync: SourceSync }) {
  const history = !s.paginated
    ? 'read whole each pass'
    : s.backfill_done
      ? `complete${s.sweeps > 1 ? ` · swept ${s.sweeps}×` : ''}`
      : s.high_water
        ? `loading · ${s.pages} page${s.pages === 1 ? '' : 's'} so far`
        : 'not started'
  const freshness = {
    filter: 'provider filter',
    newest_first: 'newest-first cut-off',
    sweep: 'full re-walk',
    whole: 'whole re-read',
  }[s.strategy]
  return (
    <div className="wz-map" style={{ marginTop: 0 }}>
      <div className="wz-maphead">
        <strong>{s.label}</strong>
        <span className="faint small">·</span>
        <Chip tone={s.backfill_done || !s.paginated ? 'ok' : s.high_water ? 'warn' : 'plain'}>
          {history}
        </Chip>
        {s.forward && <Chip tone="warn">catching up</Chip>}
      </div>
      <div className="cp-erow">
        <span className="lab">up to date as of</span>
        <span className="to small">{s.high_water ? relativeTime(s.high_water) : '—'}</span>
      </div>
      <div className="cp-erow">
        <span className="lab">new rows via</span>
        <span className="to small" title={describeSync(s)}>
          {freshness}
        </span>
      </div>
      <div className="cp-erow">
        <span className="lab">read so far</span>
        <span className="to small">
          {s.seen.toLocaleString()} row{s.seen === 1 ? '' : 's'} · {s.pages} page
          {s.pages === 1 ? '' : 's'}
          {s.last_pass_at ? ` · last pass ${relativeTime(s.last_pass_at)}` : ''}
        </span>
      </div>
    </div>
  )
}
